import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { get, del } from '../../utils/fetchAPI'; // Utility for API calls
import { FaEdit, FaTrash } from 'react-icons/fa';
import PageTitle from "../../components/page-title/pageTitle";
import {RiShieldUserLine} from "react-icons/ri";
import {AnimatePresence, motion} from "framer-motion";
import {TiUserAddOutline} from "react-icons/ti";
import CreateAdmin from "./admin-create";
import Loader from "../../components/loader/loder";
import CustomPagination from "../../components/pagination/CustomPagination";
import { swalDel } from "../../utils/SwalUti";
import { toast } from "react-toastify";

const AdminList = () => {
    const [admins, setAdmins] = useState([]);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    useEffect(() => {
        fetchAdmins();
    }, [currentPage, perPage]);

    const fetchAdmins = async () => {
        setIsLoading(true);
        await get('/list')
            .then((response) => {
                setAdmins(response?.data);
                setTotalRows(response.data?.total);
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
            }).finally(()=> {
                setIsLoading(false);
            })
    };

    const handleEdit = (admin) => {
        setSelectedAdmin(admin);
        setShowPopup(true);
    };

    const handleCreateAdmin = (newAdmin) => {
        if (selectedAdmin) {
            // Update user in the list
            setAdmins(admins.map(admin => admin._id === newAdmin._id ? newAdmin : admin));
        } else {
            // Add new users to the list
            setAdmins([newAdmin, ...admins]);
        }
        setShowPopup(false);
    };

    const handleDelete = async (admin) => {
        // Check if user type is 'admin' to allow deletion
        if (admin?.type?.toLowerCase() !== 'admin') {
            toast.error('Only admin users can be deleted.', {
                position: "top-center",
                theme: "light",
            });
            return;
        }

        const result = await swalDel(`Are you sure you want to delete "${admin.name}"? This action cannot be undone.`);
        
        if (result.isConfirmed) {
            try {
                await del(`/delete/${admin._id}`);
                toast.success('Admin deleted successfully!', {
                    position: "top-center",
                    theme: "light",
                });
                fetchAdmins();
            } catch (error) {
                console.error('Error deleting admin:', error);
                toast.error('Failed to delete admin. Please try again.', {
                    position: "top-center",
                    theme: "light",
                });
            }
        }
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row?.name,
            sortable: true,
        },
        {
            name: 'Email',
            selector: row => row?.email,
            sortable: true,
        },
        {
            name: 'Type',
            selector: row => row?.type,
            sortable: true,
        },
        {
            name: 'Phone',
            selector: row => row?.phone,
            sortable: true,
        },
        {
            name: 'Role',
            selector: row => row?.role?.name || 'N/A',
            sortable: true,
            cell: (row) => (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                    {row?.role?.name || 'No Role'}
                </span>
            ),
        },
        {
            name: 'Actions',
            selector: row => (
                <div className="flex gap-2">
                    <button
                        className="p-1 bg-blue-500 text-white rounded"
                        onClick={() => handleEdit(row)}
                    >
                        <FaEdit />
                    </button>
                    <button
                        className={`p-1 text-white rounded ${
                            row?.type?.toLowerCase() === 'admin' 
                                ? 'bg-red-500 hover:bg-red-600' 
                                : 'bg-gray-400 cursor-not-allowed'
                        }`}
                        onClick={() => handleDelete(row)}
                        disabled={row?.type?.toLowerCase() !== 'admin'}
                        title={row?.type?.toLowerCase() !== 'admin' ? 'Only admin users can be deleted' : 'Delete'}
                    >
                        <FaTrash />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#50b6b5',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 'bold',
            },
        },
        rows: {
            style: {
                fontSize: '0.9rem',
            },
            stripedStyle: {
                backgroundColor: '#f6f6f6', // Light ash for striped rows
            },
            highlightOnHoverStyle: {
                backgroundColor: '#e0e0e0', // Slightly darker shade on hover
            },
        },
    };


    return (
        <div className="flex justify-center flex-col gap-5 w-full">
            <div className="flex justify-between items-center">
                <PageTitle title="Admin List" icon={<RiShieldUserLine/>}/>
                <button
                    onClick={() => {
                        setSelectedAdmin(null);
                        setShowPopup(true);
                    }}
                    className="flex items-center justify-center gap-3 text-sm px-4 py-2 bg-primary text-white rounded-md">
                        <span className="">
                            <TiUserAddOutline className="w-5 h-5" />
                        </span>
                    Create New Admin
                </button>
            </div>
            {isLoading ? (
                <div className="text-center text-gray-500"><Loader /></div>
            ) : (
                <DataTable
                    columns={columns}
                    data={admins}
                    customStyles={customStyles}
                    highlightOnHover
                    striped
                />
            )}

            <CustomPagination
                currentPage={currentPage}
                totalRows={totalRows}
                perPage={perPage}
                setCurrentPage={setCurrentPage}
                setPerPage={setPerPage}
            />

            <AnimatePresence>
                {showPopup && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center">
                        <motion.div initial={{ scale: 0, opacity: 0 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="bg-white p-5 rounded-md shadow-lg">
                            <CreateAdmin
                                admin={selectedAdmin}
                                createAdmin={handleCreateAdmin}
                                fetchAdmins={fetchAdmins}
                                onClose={() => setShowPopup(false)}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminList;
